import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconPencil, IconTrash } from "@tabler/icons-react";

import { openDeleteModal } from "@components/DeleteModal";
import { FilePreviewModal } from "@components/FilePreviewModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useDeleteImmunization } from "@hooks/mutations/immunization/useDeleteImmunization";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { useAuth } from "@contexts/auth";

import { Immunization } from "@customTypes/immunization";

import { EditModal } from "../EditModal";


interface TableControlsProps {
  immunization: Partial<Immunization>;
}

export const TableControls = ({ immunization }: TableControlsProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [
    filePreviewOpened,
    { open: filePreviewOpen, close: filePreviewClose },
  ] = useDisclosure(false);
  const [editModalOpened, { open: editModalOpen, close: editModalClose }] =
    useDisclosure(false);
  const [immunizationState, setImmunizationState] =
    useState<Partial<Immunization> | null>(null);

  const { mutate: deleteImmunization, isLoading: isDeleteImmunizationLoading } =
    useDeleteImmunization();
  const handleErrors = useQueryErrorHandling();

  useEffect(() => {
    // console.log(row.original);
    if (immunization) {
      setImmunizationState(immunization);
    }
  }, [immunization]);

  return (
    <>
      <FilePreviewModal
        opened={filePreviewOpened}
        close={filePreviewClose}
        docs={
          immunizationState?.files
            ? [
                ...immunizationState.files.map(item => ({
                  uri: "https://" + item + `?token=${token}`,
                  fileName: (item as string).split("/files/")[1],
                })),
              ]
            : []
        }
      />
      <EditModal
        opened={editModalOpened}
        close={editModalClose}
        immunization={immunization}
      />

      <Group className="table-controls" justify="end">
        <Menu withArrow position="bottom-end" withinPortal>
          <Menu.Target>
            <Button
              variant="outline"
              size="compact-sm"
              loading={isDeleteImmunizationLoading}>
              {t("action")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              disabled={
                immunizationState?.files && immunizationState.files.length > 0
                  ? false
                  : true
              }
              leftSection={<IconEye size={16} stroke={1.5} />}
              onClick={filePreviewOpen}>
              {t("view-files")}
            </Menu.Item>

            <PermissionGate permission="preventative.edit"><Menu.Item
              leftSection={<IconPencil size={16} stroke={1.5} />}
              onClick={editModalOpen}>
              {t("edit-immunization")}
            </Menu.Item></PermissionGate>
            <PermissionGate permission="preventative.delete"><Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() => {
                openDeleteModal({
                  onConfirm: () => {
                    const id = showLoadingNotification({});
                    deleteImmunization(
                      {
                        immunizationId: immunizationState?.id as string,
                        patientId: immunizationState?.patientId as string,
                      },
                      {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "delete-immunization-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      }
                    );
                  },
                });
              }}>
              {t("delete-immunization")}
            </Menu.Item></PermissionGate>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <LastUpdatedTimestamp
        updatedDate={immunization?.updatedAt as Date}
        className="last-updated"
      />
    </>
  );
};
